import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { Badge } from 'primereact/badge'
import { Button } from 'primereact/button'
import { Card } from 'primereact/card'
import { TabPanel, TabView } from 'primereact/tabview'
import type React from 'react'
import { useNavigate } from 'react-router-dom'
import { MarkdownViewer } from '~/components/common/MarkdownViewer'
import {
  useMarkAllReadMutation,
  useMarkReadMutation,
  useNotificationHistory,
  useNotifications,
} from '~/hooks/activitiesApi'
import type { Notification } from '~/types/activity.types'
import { getActivityIcon, getActivityLink, getActivityText } from '~/utils/activity.utils'

dayjs.extend(relativeTime)

const NotificationItem: React.FC<{ notif: Notification; onRead?: (id: number) => void }> = ({
  notif,
  onRead,
}) => {
  const navigate = useNavigate()
  return (
    <button
      type='button'
      className='flex gap-4 p-4 mb-2 hover:bg-gray-50 transition-colors rounded-lg border border-gray-100 cursor-pointer text-left w-full'
      onClick={() => {
        const link = getActivityLink(notif)
        if (link !== '#') {
          navigate(link)
        }
      }}
    >
      <div className='flex items-center justify-center w-12 h-12 rounded-lg bg-gray-100'>
        <i className={`${getActivityIcon(notif.activityType)} text-xl`} />
      </div>
      <div className='overflow-hidden'>
        <div className='flex justify-between items-start'>
          <span className='font-bold text-gray-800'>
            {getActivityText(notif.activityType, notif.actor?.name || 'System')}
          </span>
          <span className='text-sm text-gray-500 whitespace-nowrap ml-2'>
            {dayjs(notif.createTs).fromNow()}
          </span>
        </div>
        <MarkdownViewer
          content={notif.message || 'New activity recorded'}
          className='mt-1'
          limitLines={2}
        />
        <div className='mt-2 flex gap-2 overflow-hidden'>
          {notif.docId && (
            <Badge value={`Doc #${notif.docId}`} severity='info' className='text-xs py-1 px-2' />
          )}
          {notif.controlId && (
            <Badge
              value={`Control #${notif.controlId}`}
              severity='warning'
              className='text-xs py-1 px-2'
            />
          )}
        </div>
      </div>
      {!notif.readTs && onRead && (
        <Button
          icon='pi pi-check'
          rounded
          text
          severity='secondary'
          aria-label='Mark as read'
          onClick={() => onRead(notif.id)}
        />
      )}
    </button>
  )
}

const NotificationsPage = () => {
  const { data: unreadNotifications, isLoading: loadingUnread } = useNotifications({
    unreadOnly: true,
    getAll: true,
  })
  const { data: historyNotifications, isLoading: loadingHistory } = useNotificationHistory({
    page: 0,
    pageSize: 50,
  })

  const markRead = useMarkReadMutation()
  const markAllRead = useMarkAllReadMutation()

  return (
    <div className='max-w-4xl mx-auto'>
      <div className='flex justify-between items-center mb-4'>
        <h1 className='text-3xl m-0'>Notifications</h1>
        {unreadNotifications && unreadNotifications.length > 0 && (
          <Button
            label='Mark all as read'
            icon='pi pi-check-circle'
            severity='secondary'
            text
            onClick={() => markAllRead.mutate()}
            disabled={markAllRead.isLoading}
          />
        )}
      </div>

      <Card className='shadow-sm'>
        <TabView>
          <TabPanel
            header='Unread'
            leftIcon='pi pi-bell mr-2'
            pt={{ headerAction: { className: 'flex items-center' } }}
          >
            {loadingUnread ? (
              <div className='text-center p-5'>
                <i className='pi pi-spin pi-spinner text-4xl text-gray-300' />
              </div>
            ) : unreadNotifications?.length === 0 ? (
              <div className='text-center p-8 text-gray-400'>
                <i className='pi pi-bell-slash text-6xl mb-4' />
                <p>No new notifications. You're all caught up!</p>
              </div>
            ) : (
              <div className='flex flex-col'>
                {unreadNotifications?.map((n) => (
                  <NotificationItem key={n.id} notif={n} onRead={(id) => markRead.mutate(id)} />
                ))}
              </div>
            )}
          </TabPanel>
          <TabPanel
            header='History'
            leftIcon='pi pi-history mr-2'
            pt={{ headerAction: { className: 'flex items-center' } }}
          >
            {loadingHistory ? (
              <div className='text-center p-5'>
                <i className='pi pi-spin pi-spinner text-4xl text-gray-300' />
              </div>
            ) : historyNotifications?.length === 0 ? (
              <div className='text-center p-8 text-gray-400'>
                <p>No notification history.</p>
              </div>
            ) : (
              <div className='flex flex-col'>
                {historyNotifications?.map((n) => (
                  <NotificationItem key={n.id} notif={n} />
                ))}
              </div>
            )}
          </TabPanel>
        </TabView>
      </Card>
    </div>
  )
}

export default NotificationsPage
